import type { TFunction } from "i18next";
import type { ViewMode } from "@/zapi-frontend/stores/chapter";
import { DEEP_DIVE_SECTIONS, getExecSummaryLabelKey } from "@/zapi-frontend/shared/lib/projectChapters";
import type { TChapter } from "@/zapi-frontend/shared/types/projects";
import type { TNavEntry, TNavGroup, TNavTarget, TProjectPageView } from "../model/types";
import { VALUE_CHAIN_SECTION, type TProjectChapterGroups } from "./groupProjectChapters";

type TNavSetupInput = {
  isLabs: boolean;
  isDev: boolean;
  debugGenState: string | null;
  isSetupActive: boolean;
  isPolling: boolean;
  isTemplateLess: boolean;
  chapterCount: number;
};

type TProjectNavInput = {
  groups: TProjectChapterGroups;
  isTemplateLess: boolean;
  isLabs: boolean;
  showSkeleton: boolean;
  isSetupBlocking: boolean;
  allChapterMemosReady: boolean;
  rqaSimEnabled: boolean;
  hasRqaSim: boolean;
  hasModelLedger: boolean;
  t: TFunction<"projectHome">;
};

type TActiveNavInput = {
  groups: TProjectChapterGroups;
  viewMode: ViewMode | undefined;
  activeChapterId?: number;
  isPresentationOpen: boolean;
};

const chapterTarget = (chapterId: number): TNavTarget => ({ kind: "chapter", chapterId });
const viewTarget = (view: TProjectPageView): TNavTarget => ({ kind: "view", view });

const chapterLabel = (chapter: TChapter) => chapter.projectTemplateChapter.nav || chapter.projectTemplateChapter.name;

const lockedEntry = (id: string, label: string): TNavEntry => ({ id, label, locked: true, target: null });

const sectionEntry = (key: string, label: string, chapters: TChapter[]): TNavEntry[] =>
  chapters.length
    ? [
        {
          id: `section-${key}`,
          label,
          testId: `sidebar-section-${key}-button`,
          locked: false,
          target: chapterTarget(chapters[0].id)
        }
      ]
    : [];

export function getNavSetupState({
  isLabs,
  isDev,
  debugGenState,
  isSetupActive,
  isPolling,
  isTemplateLess,
  chapterCount
}: TNavSetupInput) {
  const isDebugSettingUp = (isLabs || isDev) && debugGenState === "settingUp";
  return {
    showSkeleton: (isSetupActive && isTemplateLess && chapterCount === 0) || isDebugSettingUp,
    isSetupBlocking: isPolling || isSetupActive || isDebugSettingUp
  };
}

export function buildProjectNav({
  groups,
  isTemplateLess,
  isLabs,
  showSkeleton,
  isSetupBlocking,
  allChapterMemosReady,
  rqaSimEnabled,
  hasRqaSim,
  hasModelLedger,
  t
}: TProjectNavInput): TNavGroup[] {
  const { execChapter, memoChapter } = groups;

  const projectEntries: TNavEntry[] = showSkeleton
    ? [
        lockedEntry("exec", t("sidebar.executiveSummary")),
        lockedEntry("memo", t("sidebar.innoveraMemo")),
        lockedEntry("rqaSim", t("sidebar.rqaSim"))
      ]
    : [
        ...(execChapter
          ? [
              {
                id: "exec",
                label: t(getExecSummaryLabelKey(isTemplateLess)),
                testId: "sidebar-executive-summary-button",
                locked: false,
                target: chapterTarget(execChapter.id)
              }
            ]
          : []),
        ...(memoChapter
          ? [
              {
                id: "memo",
                label: t("sidebar.innoveraMemo"),
                testId: "sidebar-memo-button",
                locked: false,
                target: chapterTarget(memoChapter.id)
              }
            ]
          : []),
        ...(rqaSimEnabled
          ? [
              {
                id: "risks",
                label: t("sidebar.rqa"),
                testId: "sidebar-risks-button",
                locked: false,
                target: viewTarget("risks")
              }
            ]
          : []),
        ...(hasRqaSim
          ? [
              {
                id: "rqaSim",
                label: t("sidebar.rqaSim"),
                testId: "sidebar-rqa-sim-button",
                locked: false,
                target: viewTarget("rqaSim")
              }
            ]
          : [])
      ];

  const analysisEntries: TNavEntry[] = showSkeleton
    ? DEEP_DIVE_SECTIONS.map(({ key, labelKey }) => lockedEntry(`section-${key}`, t(labelKey)))
    : [
        ...groups.sections.flatMap((section) => sectionEntry(section.key, t(section.labelKey), section.chapters)),
        ...groups.unsectioned.map((chapter) => ({
          id: `chapter-${chapter.id}`,
          label: chapterLabel(chapter),
          testId: `chapter-${chapter.id}-item`,
          locked: false,
          target: chapterTarget(chapter.id)
        })),
        ...sectionEntry("appendices", t("sidebar.appendices"), groups.appendices),
        ...(isLabs ? sectionEntry("hidden", t("sidebar.hiddenChapters"), groups.hiddenChapters) : [])
      ];

  const navGroups: TNavGroup[] = [
    {
      id: "project",
      entries: [
        {
          id: "home",
          label: t("sidebar.projectHome"),
          testId: "sidebar-home",
          locked: false,
          target: { kind: "home" }
        },
        ...projectEntries
      ]
    },
    { id: "analysis", label: t("sidebar.analysis"), entries: analysisEntries },
    {
      id: "foundation",
      label: t("sidebar.foundation"),
      entries: [
        {
          id: "frame",
          label: t("sidebar.frame"),
          testId: "sidebar-frame-button",
          locked: false,
          target: viewTarget("projectFrame")
        },
        ...(hasModelLedger && !showSkeleton
          ? [
              {
                id: "modelLedger",
                label: t("sidebar.modelLedger"),
                testId: "sidebar-model-ledger-button",
                locked: false,
                target: viewTarget("modelLedger")
              }
            ]
          : []),
        {
          id: "vault",
          label: t("sidebar.vault"),
          testId: "sidebar-vault-button",
          locked: isSetupBlocking,
          target: viewTarget("vault")
        }
      ]
    },
    {
      id: "presentations",
      label: t("sidebar.presentations"),
      entries: [
        {
          id: "presentation",
          label: t("header.presentation"),
          testId: "sidebar-presentation-button",
          locked: isSetupBlocking && !allChapterMemosReady,
          target: { kind: "presentation" }
        }
      ]
    },
    {
      id: "artifacts",
      label: t("sidebar.artifacts"),
      entries: [
        {
          id: "exports",
          label: t("sidebar.exports"),
          testId: "sidebar-exports-button",
          locked: isSetupBlocking,
          target: viewTarget("exports")
        }
      ]
    }
  ];

  return navGroups.filter((group) => group.entries.length > 0);
}

function activeChapterNavId(groups: TProjectChapterGroups, chapterId: number) {
  const holds = (chapters: TChapter[]) => chapters.some((chapter) => chapter.id === chapterId);
  if (chapterId === groups.execChapter?.id) return "exec";
  if (chapterId === groups.memoChapter?.id) return "memo";
  const section = groups.sections.find((s) => holds(s.chapters));
  if (section) return `section-${section.key}`;
  if (holds(groups.appendices)) return "section-appendices";
  if (holds(groups.hiddenChapters)) return "section-hidden";
  return `chapter-${chapterId}`;
}

export function getActiveNavId({ groups, viewMode, activeChapterId, isPresentationOpen }: TActiveNavInput): string {
  if (isPresentationOpen) return "presentation";
  if (viewMode === "dashboard" || viewMode === "projectOverview") return "home";
  if (viewMode === "chapter") return activeChapterId === undefined ? "" : activeChapterNavId(groups, activeChapterId);
  if (viewMode === "valueChain") return `section-${VALUE_CHAIN_SECTION}`;
  if (viewMode === "projectFrame" || viewMode === "projectConfig") return "frame";
  return viewMode ?? "";
}
